/**
 * Broadcast Hook
 * Lógica de negócio do módulo de broadcast
 */
import { useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import { broadcastApi } from '../../core/admin.api'
import { useBroadcast as useBroadcastHistory } from '../../core/admin.hooks'
import { INITIAL_FORM_STATE } from './broadcast.constants'
import {
	validateBroadcastForm,
	buildBroadcastPayload,
} from './broadcast.utils'

export const useBroadcastScreen = () => {
	// Tab state
	const [activeTab, setActiveTab] = useState('send')

	// Form state
	const [form, setForm] = useState(INITIAL_FORM_STATE)

	// Modal states
	const [showPreviewModal, setShowPreviewModal] = useState(false)
	const [showDetailsModal, setShowDetailsModal] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [showResendModal, setShowResendModal] = useState(false)

	// Loading states
	const [previewLoading, setPreviewLoading] = useState(false)
	const [sendLoading, setSendLoading] = useState(false)
	const [deleteLoading, setDeleteLoading] = useState(false)

	// Data states
	const [previewData, setPreviewData] = useState(null)
	const [selectedBroadcast, setSelectedBroadcast] = useState(null)
	const [resendPreviewData, setResendPreviewData] = useState(null)

	// History hook
	const { history, historyLoading, refetchHistory } = useBroadcastHistory()

	// Form handlers
	const handleChange = useCallback((field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }))
	}, [])

	const resetForm = useCallback(() => {
		setForm(INITIAL_FORM_STATE)
	}, [])

	// Copy handlers
	const copyPushToEmail = useCallback(() => {
		setForm((prev) => ({
			...prev,
			emailSubject: prev.pushTitle,
			emailTitle: prev.pushTitle,
			emailBody: prev.pushMessage,
		}))
		toast.success('Conteúdo copiado para Email')
	}, [])

	const copyEmailToPush = useCallback(() => {
		setForm((prev) => ({
			...prev,
			pushTitle: prev.emailTitle || prev.emailSubject,
			pushMessage: prev.emailBody,
		}))
		toast.success('Conteúdo copiado para Push')
	}, [])

	// Preview handler
	const handlePreview = useCallback(async () => {
		const errors = validateBroadcastForm(form)
		if (errors.length > 0) {
			errors.forEach((err) => toast.error(err))
			return
		}

		setPreviewLoading(true)
		try {
			const payload = buildBroadcastPayload(form)
			const data = await broadcastApi.getPreview(payload)
			setPreviewData(data)
			setShowPreviewModal(true)
		} catch (err) {
			toast.error(err.response?.data?.error || 'Erro ao gerar preview')
		} finally {
			setPreviewLoading(false)
		}
	}, [form])

	// Send handler
	const handleSend = useCallback(async () => {
		setSendLoading(true)
		try {
			const payload = buildBroadcastPayload(form)
			await broadcastApi.send(payload)
			toast.success('Broadcast enviado com sucesso!')
			setShowPreviewModal(false)
			resetForm()
			refetchHistory()
			setActiveTab('history')
		} catch (err) {
			toast.error(err.response?.data?.error || 'Erro ao enviar broadcast')
		} finally {
			setSendLoading(false)
		}
	}, [form, resetForm, refetchHistory])

	// View details handler
	const handleViewDetails = useCallback((broadcast) => {
		setSelectedBroadcast(broadcast)
		setShowDetailsModal(true)
	}, [])

	const closeDetailsModal = useCallback(() => {
		setShowDetailsModal(false)
		setSelectedBroadcast(null)
	}, [])

	// Delete handlers
	const handleOpenDelete = useCallback((broadcast) => {
		setSelectedBroadcast(broadcast)
		setShowDeleteModal(true)
	}, [])

	const handleDelete = useCallback(async () => {
		if (!selectedBroadcast) return

		setDeleteLoading(true)
		try {
			await broadcastApi.delete(selectedBroadcast._id)
			toast.success('Broadcast excluído com sucesso!')
			setShowDeleteModal(false)
			setSelectedBroadcast(null)
			refetchHistory()
		} catch (err) {
			toast.error(err.response?.data?.error || 'Erro ao excluir broadcast')
		} finally {
			setDeleteLoading(false)
		}
	}, [selectedBroadcast, refetchHistory])

	const closeDeleteModal = useCallback(() => {
		setShowDeleteModal(false)
		setSelectedBroadcast(null)
	}, [])

	// Resend handlers
	const handleOpenResend = useCallback((broadcast) => {
		setSelectedBroadcast(broadcast)
		setResendPreviewData(null)
		setShowResendModal(true)
	}, [])

	const handleResendPreview = useCallback(async (payload, onSuccess) => {
		if (!selectedBroadcast) return

		setPreviewLoading(true)
		try {
			const data = await broadcastApi.getPreview(payload)
			setResendPreviewData(data)
			if (onSuccess) onSuccess()
		} catch (err) {
			toast.error(err.response?.data?.error || 'Erro ao gerar preview')
		} finally {
			setPreviewLoading(false)
		}
	}, [selectedBroadcast])

	const handleResend = useCallback(async (broadcastId, payload) => {
		if (!broadcastId || !payload) return

		setSendLoading(true)
		try {
			await broadcastApi.send(payload)
			toast.success('Broadcast reenviado com sucesso!')
			setShowResendModal(false)
			setSelectedBroadcast(null)
			setResendPreviewData(null)
			refetchHistory()
		} catch (err) {
			toast.error(err.response?.data?.error || 'Erro ao reenviar broadcast')
		} finally {
			setSendLoading(false)
		}
	}, [refetchHistory])

	const closeResendModal = useCallback(() => {
		setShowResendModal(false)
		setSelectedBroadcast(null)
		setResendPreviewData(null)
	}, [])

	// Copy handler unified
	const handleCopyToOther = useCallback((direction) => {
		if (direction === 'pushToEmail') {
			copyPushToEmail()
		} else {
			copyEmailToPush()
		}
	}, [copyPushToEmail, copyEmailToPush])

	// Fetch history
	const fetchHistory = useCallback(() => {
		refetchHistory()
	}, [refetchHistory])

	// Close preview
	const closePreview = useCallback(() => {
		setShowPreviewModal(false)
	}, [])

	return {
		// State
		activeTab,
		form,
		history,
		previewStats: resendPreviewData || previewData,

		// Loading states
		loading: deleteLoading,
		historyLoading,
		previewLoading,
		sendLoading,

		// Modal states
		showPreview: showPreviewModal,
		selectedBroadcast,
		showDetails: showDetailsModal,
		showDelete: showDeleteModal,
		showResend: showResendModal,

		// Form handlers
		setActiveTab,
		handleFormChange: handleChange,
		handleCopyToOther,
		handlePreview,
		handleSend,
		resetForm,

		// History handlers
		fetchHistory,

		// Modal handlers
		openDetails: handleViewDetails,
		closeDetails: closeDetailsModal,
		openDelete: handleOpenDelete,
		closeDelete: closeDeleteModal,
		handleDelete,
		openResend: handleOpenResend,
		closeResend: closeResendModal,
		handleResend,
		handleResendPreview,
		closePreview,
	}
}
